import { useSignOut } from '@/components/auth/sign-out-button';
import { SettingsModal } from '@/components/settings/settings-modal';
import { useSession } from '@/lib/session';
import type { Session } from '@iq/contracts/v1/auth';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@iq/ui';
import { ChevronRight, ClipboardList, LogOut, Settings as SettingsIcon } from 'lucide-react';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

function initialsFromName(input: { name?: string; email: string }): string {
  const source = input.name?.trim() || input.email;
  const parts = source
    .split(/\s+/)
    .filter((p) => p.length > 0)
    .slice(0, 2);
  if (parts.length === 0) return '·';
  return parts.map((p) => p.charAt(0).toUpperCase()).join('');
}

function tierLabel(role: Session['role'], t: ReturnType<typeof useTranslation>['t']): string {
  switch (role) {
    case 'admin':
      return t('account.tier.admin', { defaultValue: 'Admin' });
    case 'auditor':
      return t('account.tier.auditor', { defaultValue: 'Auditor' });
    case 'operator':
      return t('account.tier.operator', { defaultValue: 'Operator' });
    default:
      return t('account.tier.viewer', { defaultValue: 'Free' });
  }
}

interface AccountFooterContentProps {
  session: Session;
}

function AccountFooterContent({ session }: AccountFooterContentProps): JSX.Element {
  const { t } = useTranslation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { signOut, pending: signOutPending } = useSignOut();

  const initials = initialsFromName({
    ...(session.name ? { name: session.name } : {}),
    email: session.email,
  });
  const display = session.name?.trim() || session.email;
  const tier = tierLabel(session.role, t);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          className="flex w-full items-center gap-3 rounded-lg p-2 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-muted"
          data-testid="account-footer-trigger"
        >
          <Avatar className="size-8 shrink-0">
            {session.image ? <AvatarImage src={session.image} alt={display} /> : null}
            <AvatarFallback className="bg-secondary text-secondary-foreground text-xs font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-medium leading-tight">{display}</span>
            <span className="truncate text-muted-foreground text-xs leading-tight">{tier}</span>
          </div>
          <ChevronRight className="size-4 text-muted-foreground shrink-0" aria-hidden />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="top"
          align="end"
          sideOffset={8}
          className="min-w-[240px]"
          data-testid="account-footer-menu"
        >
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="size-9 shrink-0">
              {session.image ? <AvatarImage src={session.image} alt={display} /> : null}
              <AvatarFallback className="bg-secondary text-secondary-foreground text-xs font-medium">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium leading-tight">{display}</span>
              <span className="truncate text-muted-foreground text-xs leading-tight">
                {session.email}
              </span>
            </div>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              const tab = window.open('/tasks', '_blank');
              if (tab) tab.opener = null;
            }}
            data-testid="account-footer-tasks"
          >
            <ClipboardList className="mr-2 size-4" aria-hidden />
            <span>{t('tasks.menu')}</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              setSettingsOpen(true);
            }}
            data-testid="account-footer-settings"
          >
            <SettingsIcon className="mr-2 size-4" aria-hidden />
            <span>{t('settings.title')}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              void signOut();
            }}
            disabled={signOutPending}
            data-testid="account-footer-signout"
            className="text-destructive focus:bg-destructive/10 focus:text-destructive"
          >
            <LogOut className="mr-2 size-4" aria-hidden />
            <span>{signOutPending ? t('auth.signOut.pending') : t('auth.signOut.label')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <SettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />
    </>
  );
}

/**
 * ChatGPT-style account dropdown anchored at the sidebar footer. Shows the
 * signed-in user's avatar + display name + role; expanding the menu
 * upward exposes Settings (opens the existing modal) and Log out (uses
 * `useSignOut`, same behaviour as the standalone button).
 *
 * Renders `null` while the session query is loading or the user is
 * unauthenticated — the route guard already redirects unauthenticated
 * visitors, so this is purely defensive.
 */
export const AccountFooter: FC = () => {
  const state = useSession();
  if (state.status !== 'authenticated') return null;
  return <AccountFooterContent session={state.session} />;
};
